import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { IMAGES } from "../siteConfig";
import { Reveal, SectionHead } from "../components/Reveal";

const TIMELINE = [
  { year: "2011", title: "Mangalam Enterprises", copy: "The group is established with industrial tyre distribution at its core." },
  { year: "2022", title: "Apex Infra", copy: "Apex Infra is added to strengthen the earthmoving and OTR tyre business." },
  { year: "2025", title: "CVR Vertical", copy: "The Commercial Vehicle Radial tyre vertical is added for trucks, buses and fleets." },
  { year: "Present", title: "Apex Mineral Resources", copy: "Expansion towards integrated mining solutions through AMR — tyre management, tracking and analytics." },
];

const PILLARS = [
  { title: "Performance", copy: "Solutions designed around real operating conditions." },
  { title: "Reliability", copy: "Long-term relationships with customers and partners." },
  { title: "Service", copy: "Technical support beyond tyre supply." },
  { title: "Technology", copy: "Data-driven tyre management and operational visibility." },
];

const SEGMENTS = [
  "Mining Contractors", "Fleet Owners", "Infrastructure Companies",
  "Construction Companies", "Industrial Customers", "Commercial Vehicle Fleets",
  "Dealers & Distributors",
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-[72px]">
      <Seo
        title="About Mangalam Group — Tyre & Mining Solutions"
        description="Mangalam Group brings together Mangalam Enterprises, Apex Infra and AMR for industrial tyres, OTR solutions and tyre management from Dhanbad, Jharkhand."
        path="/about"
      />

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={IMAGES.openPit} alt="Open-pit mining operation in Jharkhand" className="h-full w-full object-cover img-industrial opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 to-obsidian" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-ember">About Mangalam Group</p>
            <h1 className="mt-5 max-w-3xl font-condensed text-4xl sm:text-5xl lg:text-6xl font-bold uppercase leading-[1.0] tracking-tight text-white">
              Built on Experience. Moving Towards the Future.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">
              From industrial tyre distribution in 2011 to an integrated tyre and
              mining-solutions group today — Mangalam Group has grown one specialised
              capability at a time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="The Journey" title="A Group Built in Stages" />
          <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-4">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.08} className="bg-obsidian">
                <div className="h-full p-7" data-testid={`timeline-${t.year.toLowerCase()}`}>
                  <p className="font-condensed text-4xl font-bold text-ember">{t.year}</p>
                  <h3 className="mt-4 font-condensed text-xl font-bold uppercase tracking-wide text-white">{t.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{t.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-obsidian-2 py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            eyebrow="Our Approach"
            title="Four Pillars"
            copy="We don't just supply tyres. We build solutions around their performance."
          />
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.07} className="bg-obsidian-2">
                <div className="h-full p-7">
                  <p className="font-mono text-[10px] tracking-[0.25em] text-ember">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-condensed text-xl font-bold uppercase tracking-wide text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Who We Serve" title="Customer Segments" />
          <Reveal className="mt-10">
            <div className="flex flex-wrap gap-3">
              {SEGMENTS.map((s) => (
                <span key={s} className="border border-white/15 px-5 py-3 font-condensed text-lg font-medium uppercase tracking-wide text-slate-200">
                  {s}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Link
              to="/contact"
              data-testid="about-cta"
              className="inline-flex items-center gap-2 bg-ember px-7 py-4 font-mono text-[11px] tracking-[0.22em] text-[#10141B] transition-colors hover:bg-ember-hover"
            >
              DISCUSS YOUR REQUIREMENTS <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
